import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FilterBar } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { Select2 } from '@/components/forms/Select2';
import type { Select2Option } from '@/components/forms/Select2';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import type { ProviderFeeValuesDto, ResultDto } from '@/api/types/providerFunds';
import { CONDITIONAL_FEE, CONDITIONAL_FEE_OPTIONS } from '@/api/types/providerFunds';
import type { ProviderFundsConfig } from './config';
import { yesterdayIso } from './config';
import { renderNumber } from './renderers';
import { providersAssetsSyncApi } from '@/api/services/providerFunds';

interface DetailsTabProps {
  config: ProviderFundsConfig;
  canProcess: boolean;
  selectedProviderId: string | null;
}

// Unconditional fee block (feesType != WithConditions): four fields laid out in a
// single row, mirroring legacy #pd-fees-without (col-md-3 × 4).
function FeeRow({ fee }: { fee: ProviderFeeValuesDto | undefined }) {
  return (
    <div className="row g-3">
      <div className="col-md-3">
        <label className="form-label">Deposit Fixed Fee</label>
        <input className="form-control" readOnly value={fee ? renderNumber(fee.depositFixedFee ?? 0) : ''} />
      </div>
      <div className="col-md-3">
        <label className="form-label">Withdrawal Fixed Fee</label>
        <input className="form-control" readOnly value={fee ? renderNumber(fee.withdrawalFixedFee ?? 0) : ''} />
      </div>
      <div className="col-md-3">
        <label className="form-label">Percentage Fee On Deposit</label>
        <input className="form-control" readOnly value={fee ? renderNumber(fee.depositPercentageFee ?? 0) : ''} />
      </div>
      <div className="col-md-3">
        <label className="form-label">Percentage Fee On Withdrawal</label>
        <input className="form-control" readOnly value={fee ? renderNumber(fee.withdrawalPercentageFee ?? 0) : ''} />
      </div>
    </div>
  );
}

// One conditional-fee column (less-than / between / greater-than). Mirrors the legacy
// _DetailsTab.cshtml #pd-fees-with layout: an inline threshold row at the top (label +
// threshold input, or Min/and/Max for the "between" band) followed by four stacked
// fields with the short "Deposit %" / "Withdrawal %" labels.
function ConditionColumn({
  threshold,
  fee,
}: {
  threshold: ReactNode;
  fee: ProviderFeeValuesDto | undefined;
}) {
  return (
    <div className="col-md-4">
      {threshold}
      <div className="mb-2">
        <label className="form-label">Deposit Fixed Fee</label>
        <input className="form-control" readOnly value={fee ? renderNumber(fee.depositFixedFee ?? 0) : ''} />
      </div>
      <div className="mb-2">
        <label className="form-label">Withdrawal Fixed Fee</label>
        <input className="form-control" readOnly value={fee ? renderNumber(fee.withdrawalFixedFee ?? 0) : ''} />
      </div>
      <div className="mb-2">
        <label className="form-label">Deposit %</label>
        <input className="form-control" readOnly value={fee ? renderNumber(fee.depositPercentageFee ?? 0) : ''} />
      </div>
      <div className="mb-2">
        <label className="form-label">Withdrawal %</label>
        <input className="form-control" readOnly value={fee ? renderNumber(fee.withdrawalPercentageFee ?? 0) : ''} />
      </div>
    </div>
  );
}

export function DetailsTab({ config, canProcess, selectedProviderId }: DetailsTabProps) {
  const toast = useToast();
  const yesterday = yesterdayIso();

  const [providerId, setProviderId] = useState<string | null>(selectedProviderId);
  const [to, setTo] = useState<string>(yesterday);
  const [applied, setApplied] = useState<{ providerId: string; to: string } | null>(
    selectedProviderId ? { providerId: selectedProviderId, to: yesterday } : null,
  );
  const [processDate, setProcessDate] = useState<string>(yesterday);

  useEffect(() => {
    if (selectedProviderId) {
      setProviderId(selectedProviderId);
      setApplied({ providerId: selectedProviderId, to: yesterday });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProviderId]);

  const providersQuery = useQuery({
    queryKey: [config.key, 'providers'],
    queryFn: config.api.getProviders,
    staleTime: Infinity,
  });
  const providerOptions: Select2Option[] = useMemo(
    () => (providersQuery.data ?? []).map((p: ResultDto) => ({ value: p.value, label: p.text })),
    [providersQuery.data],
  );

  const details = useQuery({
    queryKey: [config.key, 'details', applied],
    queryFn: () => config.api.getProviderDetails({ providerId: applied!.providerId, to: applied!.to }),
    enabled: Boolean(applied),
  });

  function changeProvider(v: string | null) {
    setProviderId(v);
    if (v) setApplied({ providerId: v, to }); // auto-load on provider change
  }

  function fetchData() {
    if (!providerId) {
      toast.warning('Please select a provider.');
      return;
    }
    setApplied({ providerId, to });
  }

  async function process() {
    if (!providerId) return;
    try {
      const res = await providersAssetsSyncApi.processProviderTransactions({ providerId, to: processDate });
      if (res.isSuccess) {
        toast.info(res.additionalMessage ?? "Processing started in the background. You'll be notified when it finishes.");
      } else {
        toast.error(res.errorMessage ?? 'Failed to start processing.');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to start processing.');
    }
  }

  const d = details.data;
  const withConditions = d?.feesType === CONDITIONAL_FEE.WithConditions;

  const fields = [
    <Select2 key="provider" label="Provider" placeholder="Select Provider" value={providerId} options={providerOptions} onChange={changeProvider} />,
    {
      node: <DateRangePicker key="to" label="To" mode="single" value={to} maxDate={yesterday} onChange={setTo} />,
      pinned: true,
      width: 160,
    },
  ];
  const actions = (
    <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" onClick={fetchData} loading={details.isFetching}>
      Fetch
    </AsyncButton>
  );

  return (
    <div>
      <div className="mb-3">
        <FilterBar card={false} fields={fields} actions={actions} inlineActions />
      </div>

      {/* Cards render even before a provider is picked — inputs stay visible and
          empty, filling in once a provider is selected / fetched. */}
      {/* Card A — Account. Legacy (_DetailsTab.cshtml) wraps these in a plain
          bordered Bootstrap card, so we add the `border` utility (custom-card is
          border:0 + shadow by default). */}
      <div className="card custom-card border mb-3">
        <div className="card-header p-3 border-bottom-0">
          <h6 className="card-title mb-0">Account</h6>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Starting Balance Date</label>
              <input className="form-control" readOnly value={d?.startingBalanceDate ?? ''} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Starting Balance</label>
              <input className="form-control" readOnly value={d ? renderNumber(d.startingBalance) : ''} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Provider Currency</label>
              <input className="form-control" readOnly value={d?.currency ?? ''} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Holding Reserves %</label>
              <input className="form-control" readOnly value={d ? renderNumber(d.holdingReserves) : ''} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Rolling Days For Refunds</label>
              <input className="form-control" readOnly value={d ? renderNumber(d.rollingDays, 0) : ''} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Ownership</label>
              <input className="form-control" readOnly value={d?.ownership ?? ''} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Last Process Date</label>
              <input className="form-control" readOnly value={d ? d.lastProcessDate || 'N/A' : ''} />
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" role="switch" checked={d?.processTransaction ?? false} disabled readOnly />
                <label className="form-check-label ms-1">Process D-1</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card B — Fee Type. Bordered like legacy (see Card A note). */}
      <div className="card custom-card border mb-3">
        <div className="card-header p-3 border-bottom-0 d-flex align-items-center justify-content-between">
          <h6 className="card-title mb-0">Fee Type</h6>
          <div style={{ minWidth: 220 }}>
            <Select2 value={d?.feesType ?? null} options={CONDITIONAL_FEE_OPTIONS.map((o) => ({ value: o.value, label: o.text }))} disabled allowClear={false} searchable={false} onChange={() => undefined} />
          </div>
        </div>
        <div className="card-body">
          {!withConditions ? (
            <FeeRow fee={d?.feesWithoutCondition} />
          ) : (
            // Mirrors legacy #pd-fees-with: three col-md-4 bands, each with an inline
            // threshold row (If Amount < / Between Min and Max / >) + four stacked fields.
            <div className="row">
              <ConditionColumn
                fee={d?.lessThan}
                threshold={
                  <div className="row align-items-center mb-2">
                    <div className="col-auto">
                      <label className="form-label mb-0">If Amount &lt;</label>
                    </div>
                    <div className="col">
                      <input
                        className="form-control"
                        readOnly
                        placeholder="Threshold"
                        value={d ? renderNumber(d.lessThan?.threshold ?? 0) : ''}
                      />
                    </div>
                  </div>
                }
              />
              <ConditionColumn
                fee={d?.betweenThan}
                threshold={
                  <div className="row align-items-center mb-2">
                    <div className="col-auto">
                      <label className="form-label mb-0">If Amount Between</label>
                    </div>
                    <div className="col">
                      <input
                        className="form-control"
                        readOnly
                        placeholder="Min"
                        value={d ? renderNumber(d.betweenThan?.threshold ?? 0) : ''}
                      />
                    </div>
                    <div className="col-auto">
                      <label className="form-label mb-0">and</label>
                    </div>
                    <div className="col">
                      <input
                        className="form-control"
                        readOnly
                        placeholder="Max"
                        value={d ? renderNumber(d.betweenThan?.thresholdMax ?? 0) : ''}
                      />
                    </div>
                  </div>
                }
              />
              <ConditionColumn
                fee={d?.greaterThan}
                threshold={
                  <div className="row align-items-center mb-2">
                    <div className="col-auto">
                      <label className="form-label mb-0">If Amount &gt;</label>
                    </div>
                    <div className="col">
                      <input
                        className="form-control"
                        readOnly
                        placeholder="Threshold"
                        value={d ? renderNumber(d.greaterThan?.threshold ?? 0) : ''}
                      />
                    </div>
                  </div>
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* Card C — Process Transactions (PA-only, gated). Legacy shows this ONLY when
          Process D-1 is OFF: automatic daily processing is disabled, so the user picks
          a reference date and runs processing manually. (Hidden when D-1 is on.) */}
      {config.hasProcessCard && canProcess && d && !d.processTransaction && (
        <div className="card custom-card mb-0">
          <div className="card-header p-3 border-bottom-0">
            <h6 className="card-title mb-0">Process Transactions</h6>
          </div>
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-4 col-lg-3">
                <DateRangePicker label="Reference Date" mode="single" value={processDate} maxDate={yesterday} onChange={setProcessDate} />
              </div>
              <div className="col-md-4 col-lg-3">
                <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-cogs" onClick={process}>
                  Process
                </AsyncButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
